import { useState } from "react";
import { motion } from "framer-motion";
import { Check, X, MapPin, MessageCircle, Car, Plane, ArrowDown } from "lucide-react";
import { waLink } from "./data";
import { Reveal, Overline } from "./Reveal";

const PLANES = [
  {
    id: "esencial", name: "Esencial", tagline: "Tenés el destino definido y querés organizarte bien", price: "Gs. 100.000", pill: "1 destino",
    features: [
      ["Ruta personalizada con paradas", true], ["3 opciones de hospedaje con links", true], ["Estimación de peajes y combustible", true],
      ["Guía de cruce fronterizo", true], ["Guía de servicios en ruta (mecánicos, grúas, gomerías)", true], ["Comparativo de destinos", false],
      ["Itinerario día por día", false], ["Links de entradas a atracciones", false], ["Acompañamiento WhatsApp en ruta", false],
    ],
  },
  {
    id: "completo", name: "Completo", tagline: "Todavía estás decidiendo entre dos destinos", price: "Gs. 250.000", pill: "Comparativo de 2 destinos", featured: true,
    features: [
      ["Ruta personalizada con paradas", true], ["5 opciones de hospedaje con links", true], ["Estimación de peajes y combustible", true],
      ["Guía de cruce fronterizo completa", true], ["Guía de servicios con contactos verificados", true], ["Comparativo de 2 destinos para elegir el mejor", true],
      ["Itinerario día por día detallado", true], ["Links de entradas + horarios por atracción", true], ["Acompañamiento WhatsApp en ruta", false],
    ],
  },
  {
    id: "premium", name: "Premium", tagline: "Máxima flexibilidad: destinos, modalidad y todo resuelto", price: "Gs. 400.000", pill: "Hasta 3 destinos + avión/combinado",
    features: [
      ["Todo lo del plan Completo", true], ["Hasta 3 destinos alternativos comparados", true], ["Opción avión o combinado incluida en el análisis", true],
      ["Recomendación de horario óptimo por atracción", true], ["Plan B ante imprevistos (clima, cortes de ruta)", true], ["Consulta previa por videollamada (30 min)", true],
      ["Hasta 2 revisiones del itinerario", true], ["Acompañamiento WhatsApp durante el viaje", true],
    ],
  },
];

const AVION = [
  "Investigación de vuelos con links directos a aerolíneas", "Opciones de hospedaje con links a plataformas", "Coordinación de transfers y traslados",
  "Itinerario personalizado con actividades y entradas", "Recomendaciones de horario óptimo por atracción", "Acompañamiento por WhatsApp durante el viaje",
];

const FORM_VALUE = { esencial: "Esencial (Gs. 100.000)", completo: "Completo (Gs. 250.000)", premium: "Premium (Gs. 400.000)", avion: "Avión o combinado (5% del presupuesto)" };

const PlanCard = ({ p, i, onPick }) => (
  <motion.div data-testid={`plan-${p.id}`} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, delay: 0.1 * i, ease: [0.16, 1, 0.3, 1] }} whileHover={{ y: -6 }}
    className={`relative flex flex-col rounded-[2rem] p-7 sm:p-8 ${p.featured ? "bg-mar text-white shadow-[0_40px_80px_-40px_rgba(27,42,92,0.8)] lg:-my-4 lg:py-12" : "border border-mar/10 bg-white text-mar"}`}>
    {p.featured && <span className="absolute -top-3 left-8 rounded-full bg-sol px-4 py-1 text-xs font-extrabold text-mar">El más elegido</span>}
    <h3 className="font-display text-3xl font-extrabold">{p.name}</h3>
    <p className={`mt-2 min-h-[48px] text-sm ${p.featured ? "text-white/85" : "text-tinta/70"}`}>{p.tagline}</p>
    <p className="mt-6 font-display text-4xl font-extrabold tracking-tight">{p.price}</p>
    <p className={`text-sm ${p.featured ? "text-white/75" : "text-tinta/60"}`}>pago único por viaje</p>
    <span className={`mt-6 inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${p.featured ? "bg-white/15" : "bg-arena"}`}><MapPin className="h-3.5 w-3.5" />{p.pill}</span>
    <ul className="mt-6 flex-1 space-y-3">
      {p.features.map(([f, ok]) => (
        <li key={f} className={`flex gap-3 text-sm leading-snug ${ok ? "" : "opacity-45 line-through decoration-1"}`}>
          {ok ? <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.featured ? "text-sol" : "text-brasil"}`} /> : <X className="mt-0.5 h-4 w-4 shrink-0" />}{f}
        </li>
      ))}
    </ul>
    <button type="button" data-testid={`plan-${p.id}-choose`} onClick={() => onPick(FORM_VALUE[p.id], "En auto")}
      className={`mt-8 flex items-center justify-center gap-2 rounded-full py-3.5 font-bold transition-transform duration-300 hover:-translate-y-0.5 ${p.featured ? "bg-barro text-white" : "bg-mar text-white"}`}>
      Elegir este plan <ArrowDown className="h-4 w-4" />
    </button>
    <a data-testid={`plan-${p.id}-whatsapp`} href={waLink(`¡Hola Jaha Brasilpe! Quiero consultar sobre el plan ${p.name} para un viaje en auto.`)} target="_blank" rel="noopener noreferrer"
      className={`mt-3 flex items-center justify-center gap-2 rounded-full py-3 text-sm font-bold transition-colors ${p.featured ? "text-white/90 hover:bg-white/10" : "text-mar hover:bg-arena"}`}>
      <MessageCircle className="h-4 w-4" /> Consultar por WhatsApp
    </a>
  </motion.div>
);

const AvionPanel = ({ onPick }) => (
  <motion.div data-testid="plan-avion" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
    className="grid overflow-hidden rounded-[2rem] bg-white lg:grid-cols-12">
    <div className="bg-mar p-8 text-white sm:p-12 lg:col-span-5">
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-sol">Avión o combinado auto + avión</p>
      <p className="mt-6 font-display text-8xl font-extrabold leading-none">5%</p>
      <p className="mt-3 text-white/85">del presupuesto total estimado del viaje</p>
      <p className="mt-8 rounded-2xl bg-white/10 p-4 text-sm">Ejemplo: un viaje de USD 1.500 implica honorarios de aprox. Gs. 550.000</p>
    </div>
    <div className="p-8 sm:p-12 lg:col-span-7">
      <h3 className="font-display text-3xl font-extrabold text-mar">Honorarios según el presupuesto de tu viaje</h3>
      <p className="mt-3 text-tinta/75">Para viajes aéreos o combinados, trabajamos con un porcentaje sobre el presupuesto total estimado. Cuanto más complejo el viaje, más valor tiene la organización.</p>
      <ul className="mt-6 grid gap-3 sm:grid-cols-2">
        {AVION.map((f) => <li key={f} className="flex gap-3 text-sm text-mar"><Check className="mt-0.5 h-4 w-4 shrink-0 text-brasil" />{f}</li>)}
      </ul>
      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <button type="button" data-testid="plan-avion-choose" onClick={() => onPick(FORM_VALUE.avion, "")}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-barro px-7 py-4 font-bold text-white transition-transform duration-300 hover:-translate-y-0.5">
          Elegir este plan <ArrowDown className="h-4 w-4" />
        </button>
        <a data-testid="plan-avion-whatsapp" href={waLink("¡Hola Jaha Brasilpe! Quiero consultar los honorarios para un viaje en avión o combinado.")} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-mar/25 px-7 py-4 font-bold text-mar transition-colors hover:bg-arena">
          <MessageCircle className="h-4 w-4" /> Consultar por WhatsApp
        </a>
      </div>
    </div>
  </motion.div>
);

export const Pricing = ({ onPick }) => {
  const [tab, setTab] = useState("auto");
  const tabs = [{ id: "auto", label: "Viaje en auto", icon: Car }, { id: "avion", label: "Avión o combinado", icon: Plane }];
  return (
    <section id="tarifas" data-testid="pricing-section" className="bg-arena py-24 lg:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
        <div className="mb-12 grid gap-6 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-7">
            <Overline>Honorarios</Overline>
            <h2 className="font-display text-4xl font-extrabold leading-[0.95] tracking-tight text-mar sm:text-6xl">
              Elegí el plan que se adapta <span className="font-serif-i font-normal text-barro">a tu viaje.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <p className="text-base leading-relaxed text-tinta/75 md:text-lg">Todos los planes incluyen investigación personalizada y links directos a cada reserva. Vos pagás directo al proveedor.</p>
          </Reveal>
        </div>
        <div className="mb-12 inline-flex rounded-full bg-white p-1.5 shadow-sm" role="tablist">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button key={id} role="tab" aria-selected={tab === id} data-testid={`pricing-tab-${id}`} onClick={() => setTab(id)}
              className={`flex items-center gap-2 rounded-full px-5 py-3 text-sm font-bold transition-colors sm:px-6 ${tab === id ? "bg-mar text-white" : "text-mar hover:bg-arena"}`}>
              <Icon className="h-4 w-4" />{label}
            </button>
          ))}
        </div>
        {tab === "auto" ? (
          <div className="grid gap-6 lg:grid-cols-3 lg:items-stretch">
            {PLANES.map((p, i) => <PlanCard key={p.id} p={p} i={i} onPick={onPick} />)}
          </div>
        ) : <AvionPanel onPick={onPick} />}
      </div>
    </section>
  );
};
